import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import { PROCESS_STATUS } from '../../../model/process-list-filter';
import { Person, Site, SiteEntry, SitePaging } from '@alfresco/js-api';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription, Subject } from 'rxjs';
import { SitesService } from '@alfresco/adf-content-services';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { LegacyContentService } from '../../../services/legacy-content.service';
import { EpmIssuePlanModel } from '../../../api/models/legacy/epm-issue-plan.model';
import { Store } from '@ngrx/store';
import { AppStore, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { PlanFilerModel } from './plan-filer.model';
import { TranslateService } from '@ngx-translate/core';
import { LegacyApiService } from '../../../api/services/legacy-api.service';
import { DynamiclistsModel } from '../../../api/models/epm/dynamiclists.model';
import { PlanService } from '../../form/services/plan.service';

@Component({
  selector: 'epm-issues-filter',
  templateUrl: './plan-filter.component.html',
  styleUrls: ['./plan-filter.component.scss']
})
export class PlanFilterComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Emitted  on filters submit.
   */
  @Output() filters: EventEmitter<PlanFilerModel> = new EventEmitter<PlanFilerModel>();

  /** Sites list for the site select options */
  sites: Site[];

  handovers: string[];

  /** Site plans list for the plan select options  */
  plans: EpmIssuePlanModel[];

  /** Expand  processes filter component */
  isExpanded = true;
  types: { name: string; ref: string }[] = [];
  currentDate = new Date();
  isStartedByOpen = false;
  isCurrAssignedToOpen = false;
  isSmallScreen: boolean = false;

  private sitesSub: Subscription;
  private plansSub: Subscription;
  private siteInputChangeSub: Subscription;
  private typeInputChangeSub: Subscription;
  private onDestroy$ = new Subject<void>();
  dynamicLists: DynamiclistsModel;

  /** Filters form */
  filtersForm = this.fb.group({
    planFormGroup: this.fb.group({
      site: new FormControl('', Validators.required),
      plan: new FormControl('')
    }),
    issuesFormGroup: this.fb.group({
      // priority: new FormControl<number | null>(null, []),
      // category: new FormControl<string>('', []),
      departments: new FormControl<string[]>(null),
      startedBefore: new FormControl<Date | null>(null),
      startedAfter: new FormControl<Date | null>(null),
      startedBy: new FormControl(''),
      currAssignedTo: new FormControl(''),
      status: new FormControl(PROCESS_STATUS.ACTIVE),
      markerColorCategory: new FormControl('status'),
      types: this.fb.array([new FormControl('any')]), // Initialize with one type field set to 'any'
      handover: new FormControl('')
    })
  });

  constructor(
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private sitesService: SitesService,
    private legacyApi: LegacyContentService,
    private store: Store<AppStore>,
    private translateService: TranslateService,
    private legacyApiService: LegacyApiService,
    private planService: PlanService
  ) {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape]).subscribe((result) => {
      this.isSmallScreen = result.matches;
    });
  }

  private _getAllowedTypeRefsForCurrentSite(): string[] {
    if (!this.dynamicLists?.data) {
      return [];
    }
    const siteId = this.filtersForm.get('planFormGroup').get('site').value;
    const list = siteId ? this.dynamicLists.data.filter((t) => t.siteid === siteId) : this.dynamicLists.data;
    return list.map((t) => t.ref);
  }

  ngOnInit(): void {
    this._fetchSites();

    if (this.planService.filtersForm) {
      this._fetchPlans(this.planService.filtersForm.get('planFormGroup').get('site').value);
    }

    this.siteInputChangeSub = this.filtersForm
      .get('planFormGroup')
      .get('site')
      .valueChanges.pipe(filter(Boolean), takeUntil(this.onDestroy$))
      .subscribe((siteId: string) => {
        this._fetchPlans(siteId);

        if (this.dynamicLists?.data) {
          this.types = this._filterProcessTypesBySiteId(this.dynamicLists).map((type) => ({
            name: type.name,
            ref: type.name
          }));
        } else {
          this.types = [];
        }
        this.typesFormArray.clear();
        this.typesFormArray.push(new FormControl('any'));
        this.handovers = [];
        this.filtersForm.get('issuesFormGroup').get('handover').setValue('');
      });

    this.legacyApiService.fetchWfTypes('epmIssueWf:issueWorkflowDataListItem').then((dynamicList) => {
      this.dynamicLists = dynamicList;
      this.types = this._filterProcessTypesBySiteId(dynamicList).map((type) => ({ name: type.name, ref: type.name }));
      this.typesFormArray.setValue(['any']);
      this.filtersForm
        .get('planFormGroup')
        .get('site')
        .valueChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((_) => {
          this.types = this._filterProcessTypesBySiteId(dynamicList).map((type) => ({ name: type.name, ref: type.name }));
          this.typesFormArray.clear();
          this.typesFormArray.push(new FormControl('any'));
        });
    });

    this.typeInputChangeSub = this.typesFormArray.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((values: string[]) => {
      const lastType = values[values.length - 1];
      if (lastType && lastType !== 'any' && this.typesFormArray.length < this.types.length + 1) {
        this.typesFormArray.push(new FormControl(''));
      }

      const selectedTypes = values.filter((type): type is string => !!type && type !== 'any');
      const includeAny = values.includes('any');
      const allowedRefs = this._getAllowedTypeRefsForCurrentSite();
      const refsToLoad = includeAny ? allowedRefs : selectedTypes.map((t) => this.dynamicLists.data.find((dl) => dl.name === t)?.ref).filter(Boolean);

      if (refsToLoad.length === 0) {
        this.handovers = [];
        this.filtersForm.get('issuesFormGroup').get('handover').setValue('');
        return;
      }

      Promise.all(refsToLoad.map((ref) => this.legacyApiService.retrieveUxData(ref)))
        .then((uxDataArray) => {
          const handoverArrays = uxDataArray.map((ux) => ux.handoverOptions || []);
          const result = includeAny
            ? Array.from(new Set(handoverArrays.flat()))
            : handoverArrays.length > 0
            ? [...new Set(handoverArrays[0])].filter((opt) => handoverArrays.every((arr) => arr.includes(opt)))
            : [];

          this.handovers = result;

          const cur = this.filtersForm.get('issuesFormGroup').get('handover').value;
          if (cur && !result.includes(cur)) {
            this.filtersForm.get('issuesFormGroup').get('handover').setValue('');
          }
        })
        .catch(() => {
          this.handovers = [];
          this.filtersForm.get('issuesFormGroup').get('handover').setValue('');
        });
    });
  }

  get typesFormArray(): FormArray {
    return this.filtersForm.get('issuesFormGroup').get('types') as FormArray;
  }

  // Get available types for a specific field, excluding types selected in other fields
  getAvailableTypes(index: number): { name: string; ref: string }[] {
    const selectedTypes = this.typesFormArray.value.filter((type: string, i: number) => type && type !== 'any' && i !== index);
    return this.types.filter((type) => !selectedTypes.includes(type.ref));
  }

  removeTypeField(index: number): void {
    if (this.typesFormArray.length > 1) {
      this.typesFormArray.removeAt(index);
    }
  }

  private _filterProcessTypesBySiteId(processTypes: DynamiclistsModel) {
    const siteId = this.filtersForm.get('planFormGroup').get('site').value;
    if (siteId && this.types) {
      return processTypes.data.filter((type) => type.siteid === siteId);
    }
    return processTypes.data;
  }

  onReset() {
    this.filtersForm.reset({
      planFormGroup: {
        site: '',
        plan: ''
      },
      issuesFormGroup: {
        status: PROCESS_STATUS.ACTIVE,
        markerColorCategory: 'status',
        types: ['any'],
        handover: ''
      }
    });
    this.typesFormArray.clear();
    this.typesFormArray.push(new FormControl('any'));
    this.planService.filtersForm = this.filtersForm;
  }

  toggleStartedBySelect() {
    // hides/shows startedBy people search
    this.isStartedByOpen = !this.isStartedByOpen;
  }

  toggleCurrAssignedToSelect() {
    // hides/shows currAssignedTo people search
    this.isCurrAssignedToOpen = !this.isCurrAssignedToOpen;
  }

  onStartedBySelect(person: Person) {
    // executed when startedBy user is selected
    this.filtersForm.patchValue({ issuesFormGroup: { startedBy: person.id } });
    this.isStartedByOpen = false;
  }

  onCurrAssignedToSelect(person: Person) {
    // executed when startedBy user is selected
    this.filtersForm.patchValue({ issuesFormGroup: { currAssignedTo: person.id } });
    this.isCurrAssignedToOpen = false;
  }

  onFiltersFormSubmit() {
    // when form is submitted
    if (this.filtersForm.valid) {
      this.filters.emit(this._getDataFromForm());
      this.isExpanded = false;
    }
    this.planService.filtersForm = this.filtersForm;
  }

  getDateFormatString() {
    return 'DD/MM/YYYY';
  }

  onGeneratePdf() {
    // create request body
    const body = this._getDataFromForm().toIssueReportRequestModel();

    // call API to generate PDF
    this.legacyApi
      .generateIssueReport(body)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(new SnackbarInfoAction(this.translateService.instant('EPM.BROWSE.PLANS.REPORT')));
        }
      });
  }

  ngOnChanges(_: SimpleChanges): void {
    // TODO change select options
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.sitesSub?.unsubscribe();
    this.plansSub?.unsubscribe();
    this.siteInputChangeSub?.unsubscribe();
    this.typeInputChangeSub.unsubscribe();
  }

  disableSubmit(): boolean {
    return !this.filtersForm.valid || !this.filtersForm.get('planFormGroup').get('plan').value;
  }

  private _getDataFromForm(): PlanFilerModel {
    const typesValue = this.filtersForm.get('issuesFormGroup').get('types').value;
    const selectedTypes = typesValue.filter((type: string) => type && type !== 'any');
    const typeValue = selectedTypes.length === 0 ? 'any' : selectedTypes.join('|');

    return new PlanFilerModel({
      planFormGroup: {
        site: this.filtersForm.get('planFormGroup').get('site').value,
        plan: this.filtersForm.get('planFormGroup').get('plan').value
      },
      issuesFormGroup: {
        // priority: this.filtersForm.get('issuesFormGroup').get('priority').value,
        // category: this.filtersForm.get('issuesFormGroup').get('category').value,
        departments: this.filtersForm.get('issuesFormGroup').get('departments').value,
        startedBefore: this.filtersForm.get('issuesFormGroup').get('startedBefore').value,
        startedAfter: this.filtersForm.get('issuesFormGroup').get('startedAfter').value,
        startedBy: this.filtersForm.get('issuesFormGroup').get('startedBy').value,
        currAssignedTo: this.filtersForm.get('issuesFormGroup').get('currAssignedTo').value,
        status: this.filtersForm.get('issuesFormGroup').get('status').value,
        markerColorCategory: this.filtersForm.get('issuesFormGroup').get('markerColorCategory').value,
        type: typeValue,
        types: selectedTypes,
        handover: this.filtersForm.get('issuesFormGroup').get('handover').value
      }
    });
  }

  private _fetchSites() {
    this.sitesSub = this.sitesService
      .getSites()
      .pipe(map((sitePaging: SitePaging) => sitePaging.list.entries.map((siteEntry: SiteEntry) => siteEntry.entry)))
      .subscribe((sites: Site[]) => {
        this.sites = sites;
      });
  }

  private removeFileExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '');
  }

  private _fetchPlans(siteid: string) {
    if (siteid) {
      this.plansSub = this.legacyApi.getIssuePlans(siteid).subscribe((plans) => {
        this.plans = plans.map((plan) => ({
          ...plan,
          name: this.removeFileExtension(plan.name)
        }));
        if (this.planService.filtersForm) {
          this.filtersForm.patchValue(this.planService.filtersForm.value);
          this.planService.filtersForm = null;
        }
      });
    }
  }
}
